import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { DispatchingRule } from '@app-types/api/dispatching';
import { DispatchingRuleClient } from '@app-services/api/clients/dispatchingrule.client';
import { MailAccountContract } from '@app-types/api/mail-account';
import { MatDialog } from '@angular/material/dialog';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { MatchError } from 'src/app/services/errors/error-matcher';
import {
  MAIL_ACCOUNT_ID,
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { DrawerService } from '@app-services/drawer.service';
import { DispatchingRuleCreateOrUpdateDialogComponent } from '@app-components/settings/organization/mail-account/dispatching-rule/dispatching-rule-create-or-update-dialog/dispatching-rule-create-or-update-dialog.component';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { FormatDispatchingRuleSearchOrders } from '@app-pipes/format-dispatching-rule-search-orders.pipe';
import { DispatchingRuleSearchOrder } from '@app-types/enums/DispatchingRuleSearchOrder';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-dispatching-rule',
  templateUrl: './dispatching-rule.component.html',
  styleUrls: ['./dispatching-rule.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatTooltipModule,
    MatCardModule,
    DispatchingRuleCreateOrUpdateDialogComponent,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
  ],
})
export class DispatchingRuleComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number;
  public mailAccountId: number;
  public isLoading = true;
  public isError = false;
  public dataSource = new MatTableDataSource<DispatchingRule>([]);
  public displayedColumns: string[] = ['name', 'priority', 'actions'];
  public mailAccount: MailAccountContract;
  public searchValue = '';
  public pageSize = PAGE_SIZE;
  public stepFetch = STEP_FETCH;
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public totalSize = 0;
  public searchOrder: DispatchingRuleSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterFields<DispatchingRuleSearchOrder> = {
    ...defaultFilter,
  };
  public isExporting = false;
  public editingDispatchingRule: DispatchingRule | null = null;
  public useMobileView = false;
  private useMobileViewSubscription: Subscription;
  private formatDispatchingRuleSearchOrders =
    new FormatDispatchingRuleSearchOrders();
  isReloadLoading: boolean = false;
  private suppressSortDrivenSearch = false;
  @ViewChild('createDrawer') createDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public matchError: MatchError,
    protected route: ActivatedRoute,
    private dispatchingRuleClient: DispatchingRuleClient,
    public dialog: MatDialog,
    private mobileObserverService: MobileObserverService,
    private drawerService: DrawerService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.mailAccountId = parseInt(
      this.route.snapshot.paramMap.get(MAIL_ACCOUNT_ID) as string,
      10
    );

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatDispatchingRuleSearchOrders.transform(key),
        name: key,
      }));

    await this.loadDispatchingRules(true);
  }

  async loadDispatchingRules(fetchSearchCount: boolean): Promise<any> {
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const nullableSearchValue = this.searchValue ? this.searchValue : null;

      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: this.searchValue ? this.searchValue : null,
      };
      const response =
        await this.dispatchingRuleClient.searchForAccount(request);

      this.dataSource.data =
        this.offset === 0
          ? response.data
          : [...this.dataSource.data, ...response.data];

      if (fetchSearchCount) {
        const countRequest = new BaseGetSearchCountByIdRequest(
          this.mailAccountId,
          nullableSearchValue
        );
        const counterResponse =
          await this.dispatchingRuleClient.getSearchForAccountCount(
            countRequest
          );
        this.totalSize = counterResponse.result;
      }
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  async onDeleteBtnClicked(dispatchingRuleId: number): Promise<void> {
    await this.dispatchingRuleClient.delete({ id: dispatchingRuleId });
  }

  async deleteDispatchingRule(
    dispatchingRuleId?: number,
    dispatchingRuleName?: string
  ): Promise<void> {
    if (!dispatchingRuleName || !dispatchingRuleId) {
      return;
    }
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'confirmDispatchingRuleDeletion',
        subTitles: [
          {
            title: 'deleteDispatchingRule',
            subTitle: dispatchingRuleName,
          },
        ],
        onDelete: async () => await this.onDeleteBtnClicked(dispatchingRuleId),
      },
    });

    dialogRef.afterClosed().subscribe(async x => {
      if (x?.isDeleted) {
        await this.onSearch();
      }
    });
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public openCreateModalForm(): void {
    this.drawerService.openDrawer(this.createDrawer);
  }

  public async onCreateDispatchingRule(): Promise<void> {
    this.totalSize += 1;
    await this.loadDispatchingRules(false);
  }

  public openEditModalForm(editingDispatchingRule: DispatchingRule): void {
    this.editingDispatchingRule = editingDispatchingRule;
    this.drawerService.openDrawer(this.createDrawer);
  }

  public onCloseEditModalForm(): void {
    this.editingDispatchingRule && (this.editingDispatchingRule = null);
  }

  public countTo(): string {
    return this.dataSource.data.length.toString();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadDispatchingRules(false);
  }

  async refreshData(): Promise<void> {
    await this.loadDispatchingRules(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadDispatchingRules(fetchSearchCounter);
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as DispatchingRuleSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatDispatchingRuleSearchOrders.transform(sort.active)
      : DispatchingRuleSearchOrder.OrderByDispatchingRuleId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    this.isExporting = true;
    try {
      // const request = {
      //   offset: 1,
      //   fetch: 1,
      //   filter: this.filter,
      //   search: this.searchValue,
      //   organizationId: this.mailAccountId,
      // };

      const fileResponse = await this.dispatchingRuleClient.exportExcel(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadDispatchingRules(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
